import { useState, useRef, useEffect } from "react";
import { createPortal } from 'react-dom';
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { UserSessionSelector } from 'store/UserSession';

import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { getRoleLabel } from 'utils/Constants';

import TokenService from "config/token.service";
import { fetchUser } from "store/UserSession/features";
import SidebarWebsiteDropdown from "../../AdminClient/SidebarWebsiteDropdown";

import styles from "../../WriterClient/sidebar/SidebarWriter.module.css";

function Item({ to, icon: Icon, label, active, onClick, right, className = "", ariaControls, ariaExpanded, style }) {
  const Comp = to ? Link : "button";
  const commonProps = {
    onClick,
    className: `${styles['sb-item']} ${active ? styles.active : ""} ${className}`,
    "aria-current": active ? "page" : undefined,
    "aria-controls": ariaControls,
    "aria-expanded": ariaExpanded,
    style,
  };
  return (
    <Comp {...(to ? { to } : { type: "button" })} {...commonProps}>
      {Icon && <Icon className={styles['sb-ic']} aria-hidden="true" />}
      <span className={styles['sb-label']}>{label}</span>
      {right}
    </Comp>
  );
}

export default function SidebarProjectManager() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  let { user } = useSelector(UserSessionSelector);

  const is = (p) => pathname === p;

  const [openProjects, setOpenProjects] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const handleLogout = () => {
    TokenService.removeUser();
    dispatch(fetchUser(null));
  };

  const toggleProjects = () => setOpenProjects((v) => !v);
  const toggleSidebar = () => setIsCollapsed((v) => !v);

  useEffect(() => {
    if (openUserMenu && userMenuRef.current) {
      const rect = userMenuRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom - 115,
        left: rect.right + 4
      });
    }
  }, [openUserMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openUserMenu) return;

      const dropdown = document.querySelector('[data-user-menu-dropdown]');
      const isClickInsideDropdown = dropdown && dropdown.contains(event.target);
      const isClickInsideButton = userMenuRef.current && userMenuRef.current.contains(event.target);

      if (!isClickInsideDropdown && !isClickInsideButton) {
        setOpenUserMenu(false);
      }
    };

    if (openUserMenu) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openUserMenu]);

  return (
    <aside
      className={`sb-root  ${isCollapsed ? styles['sb-root--collapsed'] : ''}`}
      aria-label="Sidebar navigation"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: isCollapsed ? 'column' : 'row',
          padding: '0 6px 0 20px',
          height: '40px',
        }}
      >
        <Link
          to="/ProjectManager/MainMenu"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isCollapsed ? '0' : '110px',
          }}
        >
          <img
            className="logo-m"
            src="Images/new1-logo.png"
            alt="TYPIX logo"
            style={{ maxWidth: '80px', height: 'auto', display: 'block' }}
          />
        </Link>
        {!isCollapsed && (
          <img
            src="Images/icon-menu/icone-close-menu.png"
            alt="menu icon"
            onClick={toggleSidebar}
            style={{
              width: '35px',
              height: '33px',
              cursor: 'pointer',
              marginLeft: '90px',
            }}
          />
        )}
        {isCollapsed && (
          <img
            src="Images/icon-menu/icon-open-menu.png"
            alt="menu icon"
            onClick={toggleSidebar}
            style={{
              width: '35px',
              height: '33px',
              cursor: 'pointer',
              marginTop: '8px',
            }}
          />
        )}
      </div>

      <SidebarWebsiteDropdown isCollapsed={isCollapsed} canAddWebsite={false} />

      <div className="sb-section" style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0, padding: '0 14px', gap: '8px' }}>
        <div className="sb-section-title" >
          ANALYSES
        </div>
        <nav className={styles['sb-nav']}>
          <Item
            to="/ProjectManager/MainMenu"
            icon={() => <img src="/Images/sidebar-icons/dashboard.png" alt="Dashboard" className="sb-menu-item-icon" />}
            label={t("Tableau de bord")}
            active={is("/ProjectManager/MainMenu")}
            className="sb-menu-item"
          />
        </nav>

        <div className="sb-section-title" >
          PROJETS
        </div>
        <nav className={styles['sb-nav']}>
          <Item
            icon={() => <img src="/Images/sidebar-icons/projet.png" alt="Projet" className="sb-menu-item-icon" />}
            label={t("Projets")}
            onClick={toggleProjects}
            right={<ExpandMoreIcon className={`${styles['sb-caret']} ${openProjects ? styles.open : ""}`} aria-hidden="true" />}
            ariaControls="submenu-projects"
            ariaExpanded={openProjects}
            className="sb-menu-item"
          />
          <div id="submenu-projects" className={`${styles['sb-subwrap']} ${openProjects ? styles.open : ""}`} role="region" aria-hidden={!openProjects} >
            <div className={styles['sb-connector']} />
            <div className={styles['sb-sub']}>
              <Item to="/ProjectManager/ProjectsEnCours" label={t("En cours")} active={is("/ProjectManager/ProjectsEnCours")} className={styles['sb-subitem']} />
              <Item to="/ProjectManager/ProjectsEnRetard" label={t("En retard")} active={is("/ProjectManager/ProjectsEnRetard")} className={styles['sb-subitem']} />
              <Item to="/ProjectManager/ProjectsValides" label={t("Validés")} active={is("/ProjectManager/ProjectsValides")} className={styles['sb-subitem']} />
            </div>
          </div>
        </nav>
        <Item
          to="/ProjectManager/UserManagement"
          icon={() => (
            <img
              src="/Images/sidebar-icons/gestion_users.png"
              alt="Gestion Users"
              className="sb-menu-item-icon"
            />
          )}
          label={t("Mon équipe")}
          active={pathname.startsWith("/ProjectManager/UserManagement")}
          className="sb-menu-item"
        />
      </div>

      <div className={styles['sb-user-profile']} style={{ position: 'relative', marginTop: 'auto', marginBottom: '0' }}>
        <div
          ref={userMenuRef}
          onClick={(e) => {
            e.stopPropagation();
            setOpenUserMenu(!openUserMenu);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 12px',
            backgroundColor: '#FDFDFC',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            cursor: 'pointer'
          }}
        >
          <img
            src="Images/profile-icon-img.png"
            alt=""
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#1f2937',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.firstName?.toLowerCase()}.{user?.lastName?.toLowerCase()}...
            </div>
          </div>
          <div style={{
            backgroundColor: '#f3f4f6',
            borderRadius: '6px',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UnfoldMoreIcon style={{ fontSize: '16px', color: '#1f2937' }} />
          </div>
        </div>

        {openUserMenu && createPortal(
          <div
            data-user-menu-dropdown
            style={{
              position: 'fixed',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: '220px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              zIndex: 99999
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Item
              to="/ProjectManager/MonProfil"
              icon={ManageAccountsOutlinedIcon}
              label={t("Paramètres du compte")}
              active={is("/ProjectManager/MonProfil")}
              onClick={() => setOpenUserMenu(false)}
              style={{ borderBottom: '1px solid #f3f4f6', padding: '12px 16px' }}
            />
            <Item
              icon={LogoutOutlinedIcon}
              label={t("Se déconnecter")}
              onClick={() => {
                TokenService.removeUser();
                dispatch(fetchUser(null));
                setOpenUserMenu(false);
              }}
              className={styles['sb-logout']}
              style={{ padding: '12px 16px' }}
            />
          </div>,
          document.body
        )}
      </div>
    </aside>
  );
}


